import { Icon } from "./Icon";
import type { Level, Term } from "../lib/curriculum";
import { levelProgress } from "../lib/curriculum";

/**
 * Where the child has reached, in the words a parent uses.
 *
 * This is the answer to the question the app could not previously answer. The
 * mastery map said how well things were going; nothing said HOW FAR ALONG they
 * were, because the day's activities were drawn at random from a flat pool and
 * there was no "along" to be. A level, a term, a day count and the term's goals
 * are what turn a stream of activities into a year with a shape.
 *
 * Days are days WORKED. A family that took a fortnight off has not fallen
 * behind — they have done fewer days, and the number says so honestly rather
 * than accusing them of it.
 */
export function LevelProgress({
  level,
  terms,
  daysWorked,
  compact = false,
}: {
  level: Level | null;
  terms: Term[];
  daysWorked: number;
  /** The one-line form, for a card that is not the page's subject. */
  compact?: boolean;
}) {
  if (!level) return null;
  const progress = levelProgress(daysWorked, level, terms);
  const term = progress.term;
  const termPercent = Math.round((progress.dayInTerm / Math.max(progress.termDays, 1)) * 100);

  if (compact) {
    return (
      <p className="level-progress-line">
        <Icon name="calendar" size={14} />
        <strong>{level.title}</strong>
        <span>
          Day {progress.dayIndex} of {progress.total}
          {term ? ` · ${term.title} of ${progress.termCount}` : ""}
        </span>
      </p>
    );
  }

  return (
    <section className="level-progress glass-card">
      <header>
        <div>
          <small>{level.subtitle ?? "Your child's level"}</small>
          <h3>{level.title}</h3>
        </div>
        <div className="level-progress-count">
          <strong>
            Day {progress.dayIndex}
          </strong>
          <small>of {progress.total}</small>
        </div>
      </header>

      <div
        className="level-progress-bar"
        role="progressbar"
        aria-valuenow={termPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={term ? `${term.title} progress` : "Level progress"}
      >
        <i style={{ width: `${Math.min(100, Math.max(2, termPercent))}%` }} />
      </div>

      {term ? (
        <div className="level-progress-term">
          <p className="level-progress-termline">
            <strong>
              {term.title} of {progress.termCount}
            </strong>
            <span>
              day {progress.dayInTerm} of {progress.termDays}
            </span>
          </p>
          {term.goal_summary ? <p className="level-progress-summary">{term.goal_summary}</p> : null}
          {term.goals.length > 0 ? (
            <>
              <p className="level-progress-goals-title">By the end of this term</p>
              <ul className="level-progress-goals">
                {term.goals.map((goal) => (
                  <li key={goal}>
                    <Icon name="check" size={14} />
                    <span>{goal}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
